import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { X, Loader2, Link2, Upload, Film } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";

import RichTextEditor from "@/components/RichTextEditor";
const TYPES = ["podcast", "vlog", "audio", "video", "interview", "workshop"];

export default function MediaFormModal({ item, onClose, onSaved }) {
  const editing = !!item?.id;
  const [form, setForm] = useState({
    title: item?.title || "", media_type: item?.media_type || "podcast",
    video_embed_url: item?.video_embed_url || "", media_url: item?.media_url || "",
    description: item?.description || "",
    host: item?.host || "", featured_image: item?.featured_image || "",
    visibility: item?.visibility || "public", published: item?.published !== false,
    access_level: item?.access_level || (item?.visibility && item.visibility !== "public" ? "members" : "public"),
    linked_event_id: item?.linked_event_id || "", audio_url: item?.audio_url || "",
    tags: (item?.tags || []).join(", "), linked_service_id: item?.linked_service_id || "",
  });
  const { data: hub } = useQuery({ queryKey: ["media-hub-manage"], queryFn: () => api.get("/media-hub/manage").then((r) => r.data) });
  const [mode, setMode] = useState(item?.media_url ? "upload" : "link"); // link | upload
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const pickFile = () => fileRef.current?.click();
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith("video");
    const isAudio = file.type.startsWith("audio");
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(isVideo ? "/uploads/video" : isAudio ? "/uploads/audio" : "/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => (isAudio ? { ...f, audio_url: data.url, media_url: "", video_embed_url: "" } : { ...f, media_url: data.url, video_embed_url: "" }));
      toast.success(`${isVideo ? "Video" : isAudio ? "Audio" : "Image"} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "We couldn't upload that file. Please check it and try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Add a title."); return; }
    if (!form.video_embed_url.trim() && !form.media_url.trim() && !form.audio_url.trim()) {
      toast.error("Paste a link OR upload a video, audio or photo file."); return;
    }
    const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    if (payload.access_level === "ticket" && !payload.linked_event_id) { toast.error("Pick the event whose ticket holders can access this."); return; }
    if (mode === "upload") payload.video_embed_url = "";
    else payload.media_url = "";
    setLoading(true);
    try {
      if (editing) await api.put(`/media/${item.id}`, payload);
      else await api.post("/media", payload);
      toast.success(editing ? "Media updated" : "Added to the media library");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm overflow-y-auto py-10" data-testid="media-form-modal">
      <div className="relative w-full max-w-lg bg-[#F9F7F3] rounded-3xl shadow-2xl p-8 my-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        <h3 className="font-serif text-2xl text-[#1A242B] mb-1">{editing ? "Edit media" : "Add podcast / video"}</h3>
        <p className="text-sm text-[#4A5D6B] mb-4">Link an external video/podcast, or upload your own photo or video. It appears on your profile and in the ecosystem media library.</p>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Title" value={form.title} onChange={set("title")} data-testid="media-title" className="eyw-input w-full" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.media_type} onChange={set("media_type")} data-testid="media-type" className="eyw-input w-full capitalize">
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={form.access_level} onChange={set("access_level")} data-testid="media-access" aria-label="Who can access" className="eyw-input w-full">
              <option value="public">Public</option>
              <option value="members">Members only</option>
              <option value="ticket">Ticket holders only</option>
            </select>
          </div>
          <select value={form.linked_event_id} onChange={set("linked_event_id")} data-testid="media-linked-event" aria-label="Linked event" className="eyw-input w-full">
            <option value="">{form.access_level === "ticket" ? "Choose the event (required for ticket holders)" : "Link to an event (optional)"}</option>
            {(hub?.events || []).map((ev) => <option key={ev.id} value={ev.id}>{ev.title} · {ev.date}</option>)}
          </select>
          <select value={form.linked_service_id} onChange={set("linked_service_id")} data-testid="media-linked-offering" aria-label="Linked offering" className="eyw-input w-full">
            <option value="">Link to an offering (optional)</option>
            {(hub?.offerings || []).map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
          </select>
          <input placeholder="Topics, comma separated — e.g. meditation, breathwork" value={form.tags} onChange={set("tags")} data-testid="media-tags-input" aria-label="Topics" className="eyw-input w-full" />

          {/* Source toggle */}
          <div className="flex gap-2 rounded-full bg-white border border-[#E5E0D8] p-1">
            <button type="button" onClick={() => setMode("link")} data-testid="media-mode-link"
              className={`flex-1 rounded-full py-1.5 text-sm inline-flex items-center justify-center gap-1.5 transition-colors ${mode === "link" ? "bg-[#144F75] text-white" : "text-[#4A5D6B]"}`}>
              <Link2 size={14} /> Paste a link
            </button>
            <button type="button" onClick={() => setMode("upload")} data-testid="media-mode-upload"
              className={`flex-1 rounded-full py-1.5 text-sm inline-flex items-center justify-center gap-1.5 transition-colors ${mode === "upload" ? "bg-[#144F75] text-white" : "text-[#4A5D6B]"}`}>
              <Upload size={14} /> Upload a file
            </button>
          </div>

          {mode === "link" ? (
            <input placeholder="Link / embed URL (YouTube, Spotify, Vimeo, podcast…)" value={form.video_embed_url} onChange={set("video_embed_url")} data-testid="media-url" className="eyw-input w-full" />
          ) : (
            <div>
              <button type="button" onClick={pickFile} disabled={uploading} data-testid="media-upload-btn"
                className="eyw-btn-ghost w-full !py-3 disabled:opacity-60">
                {uploading ? <Loader2 className="animate-spin" size={16} /> : <Film size={16} />}
                {form.media_url ? "Replace video / photo" : "Choose a video or photo"}
              </button>
              {form.media_url && (
                <div className="mt-2 rounded-xl overflow-hidden border border-[#E5E0D8] bg-black">
                  {/\.(mp4|webm|mov|m4v|ogv)$/i.test(form.media_url)
                    ? <video src={form.media_url} controls className="w-full max-h-56" data-testid="media-upload-preview" />
                    : <img src={form.media_url} alt="" className="w-full max-h-56 object-contain" data-testid="media-upload-preview" />}
                </div>
              )}
              <input ref={fileRef} type="file" accept="video/*,audio/*,image/*" onChange={handleFile} className="hidden" />
            </div>
          )}

          <ImageUpload value={form.featured_image} onChange={(u) => setForm((f) => ({ ...f, featured_image: u }))} label="Cover image (optional)" testid="media-cover" />
          <input placeholder="Host (optional)" value={form.host} onChange={set("host")} className="eyw-input w-full" />
          <RichTextEditor value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} testid="media-desc" placeholder="Description (optional)" minHeight={100} enableBlocks={false} media={false} />
          <label className="flex items-center gap-2 text-sm text-[#4A5D6B]"><input type="checkbox" checked={form.published} onChange={set("published")} data-testid="media-published" /> Published <span className="text-xs text-[#8FA89B]">(untick to keep it as a draft, hidden from the Media page)</span></label>
          <button type="submit" disabled={loading || uploading} data-testid="media-save" className="eyw-btn w-full disabled:opacity-60">
            {loading && <Loader2 className="animate-spin" size={18} />} {editing ? "Save changes" : "Add to library"}
          </button>
        </form>
      </div>
    </div>
  );
}
